import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider } from "@/contexts/theme-provider";
import { Navigate, Route, Routes } from "react-router-dom";
import { QueryParamProvider } from "use-query-params";
import { ReactRouter6Adapter } from "use-query-params/adapters/react-router-6";
import "./App.css";
import { RouteProtected } from "./components/route-protected";
import { Toaster } from "./components/ui/toaster";
import { AdminModeProvider } from "./contexts/admin-mode-provider";
import { ChatStreamProvider } from "./contexts/chat-stream-context";
import { CreditsProvider } from "./contexts/credits-provider";
import { HubSpotIntegrationProvider } from "./contexts/hubspot-integration-provider";
import { SeliarisIntegrationProvider } from "./contexts/seliaris-integration-provider";
import { CreateListingDialogProvider } from "./contexts/create-listing-dialog-provider";
import { ListingsProvider } from "./contexts/listings-provider";
import { PricesProvider } from "./contexts/prices-provider";
import { TeamsProvider } from "./contexts/teams-provider";
import { UserProvider } from "./contexts/user-provider";
import { UsersProvider } from "./contexts/users-provider";
import { Layout } from "./Layout";
import { CheckoutCancelledPage } from "./pages/checkout-cancelled";
import { CheckoutSuccessPage } from "./pages/checkout-success";
import ChatMobilePage from "./pages/mobile/chat";
import CompanyMobilePage from "./pages/mobile/company";
import ListingMobilePage from "./pages/mobile/listing";
import ListingContentMobilePage from "./pages/mobile/listing/content";
import NotFoundPage from "./pages/shared/not-found";
import ReferralsPage from "./pages/shared/referrals";
import { SetupTeamCreationPage } from "./pages/shared/setup/team-creation";
import { SetupTeamCreationBilling } from "./pages/shared/setup/team-creation-billing";
import { SetupPaymentFailedPage } from "./pages/shared/setup/payment-failed";
import { SetupPaymentFailedBillingPage } from "./pages/shared/setup/payment-failed-billing";
import { SetupResubscriptionPage } from "./pages/shared/setup/resubscription";
import { SetupResubscriptionBilling } from "./pages/shared/setup/resubscription-billing";
import { SetupTrialEndedPage } from "./pages/shared/setup/trial-ended";
import { SetupTrialEndedBilling } from "./pages/shared/setup/trial-ended-billing";

export function AppMobile({ home }: { home: React.ReactNode }) {
  return (
    <>
      <ThemeProvider>
        <AdminModeProvider>
          <TooltipProvider>
              <TeamsProvider>
                <UserProvider>
                  <UsersProvider>
                    <CreditsProvider>
                      <PricesProvider>
                      <ListingsProvider>
                        <CreateListingDialogProvider>
                        <HubSpotIntegrationProvider>
                        <SeliarisIntegrationProvider>
                          <ChatStreamProvider>
                            <QueryParamProvider adapter={ReactRouter6Adapter}>
                              <Routes>
                                <Route path="/">
                                  <Route index path="/" element={home} />
                                  <Route
                                    path="checkout-success"
                                    element={<CheckoutSuccessPage />}
                                  />
                                  <Route
                                    path="checkout-cancelled"
                                    element={<CheckoutCancelledPage />}
                                  />
                                  <Route
                                    path="setup"
                                    element={<Layout showNavMain={false} showNavSearch={false} />}
                                  >
                                    <Route
                                      index
                                      element={<Navigate to="/" replace />}
                                    />
                                    <Route
                                      path="team-creation"
                                      element={
                                        <RouteProtected
                                          licenceActiveState={false}
                                          element={<SetupTeamCreationPage />}
                                        />
                                      }
                                    />
                                    <Route
                                      path="trial-ended"
                                      element={
                                        <RouteProtected
                                          requiredRoles={[
                                            "admin",
                                            "advisor",
                                            "manager",
                                          ]}
                                          licenceActiveState={false}
                                          element={<SetupTrialEndedPage />}
                                        />
                                      }
                                    />
                                    <Route
                                      path="team-creation-billing"
                                      element={
                                        <RouteProtected
                                          requiredRoles={[
                                            "billing",
                                            "manager",
                                            "admin",
                                            "advisor",
                                          ]}
                                          licenceActiveState={false}
                                          element={<SetupTeamCreationBilling />}
                                        />
                                      }
                                    />
                                    <Route
                                      path="trial-ended-billing"
                                      element={
                                        <RouteProtected
                                          requiredRoles={["billing"]}
                                          licenceActiveState={false}
                                          element={<SetupTrialEndedBilling />}
                                        />
                                      }
                                    />
                                    <Route
                                      path="payment-failed"
                                      element={
                                        <RouteProtected
                                          requiredRoles={[
                                            "admin",
                                            "advisor",
                                            "manager",
                                          ]}
                                          licenceActiveState={false}
                                          element={<SetupPaymentFailedPage />}
                                        />
                                      }
                                    />
                                    <Route
                                      path="payment-failed-billing"
                                      element={
                                        <RouteProtected
                                          requiredRoles={["billing"]}
                                          licenceActiveState={false}
                                          element={
                                            <SetupPaymentFailedBillingPage />
                                          }
                                        />
                                      }
                                    />
                                    <Route
                                      path="resubscription"
                                      element={
                                        <RouteProtected
                                          requiredRoles={[
                                            "admin",
                                            "advisor",
                                            "manager",
                                          ]}
                                          licenceActiveState={false}
                                          element={<SetupResubscriptionPage />}
                                        />
                                      }
                                    />
                                    <Route
                                      path="resubscription-billing"
                                      element={
                                        <RouteProtected
                                          requiredRoles={["billing"]}
                                          licenceActiveState={false}
                                          element={
                                            <SetupResubscriptionBilling />
                                          }
                                        />
                                      }
                                    />
                                  </Route>
                                  <Route element={<Layout />}>
                                    <Route
                                      path="chat"
                                      element={
                                        <RouteProtected
                                          requiredLicences={[
                                            "basic",
                                            "professional",
                                          ]}
                                          licenceActiveState={true}
                                          element={<ChatMobilePage />}
                                        />
                                      }
                                    >
                                      <Route index />
                                      <Route path=":conversationId" />
                                    </Route>
                                    {/* No prospecting module on mobile: the
                                        chat is the way in. Old links land
                                        there instead of on a missing page. */}
                                    <Route
                                      path="prospecting/*"
                                      element={<Navigate to="/chat" replace />}
                                    />
                                    <Route
                                      path="company/:companyId"
                                      element={
                                        <RouteProtected
                                          requiredLicences={["professional"]}
                                          licenceActiveState={true}
                                          element={<CompanyMobilePage />}
                                        />
                                      }
                                    />
                                    <Route
                                      path="listing"
                                      element={
                                        <RouteProtected
                                          requiredLicences={["professional"]}
                                          licenceActiveState={true}
                                          element={<ListingMobilePage />}
                                        />
                                      }
                                    />
                                    <Route
                                      path="listing/:listingId"
                                      element={
                                        <RouteProtected
                                          requiredLicences={["professional"]}
                                          licenceActiveState={true}
                                          element={<ListingContentMobilePage />}
                                        />
                                      }
                                    />

                                    <Route
                                      path="referrals"
                                      element={<ReferralsPage />}
                                    />

                                    <Route
                                      path="*"
                                      element={<NotFoundPage />}
                                    />
                                  </Route>
                                </Route>
                              </Routes>
                            </QueryParamProvider>
                            <Toaster />
                          </ChatStreamProvider>
                        </SeliarisIntegrationProvider>
                        </HubSpotIntegrationProvider>
                        </CreateListingDialogProvider>
                      </ListingsProvider>
                      </PricesProvider>
                    </CreditsProvider>
                  </UsersProvider>
                </UserProvider>
              </TeamsProvider>
          </TooltipProvider>
        </AdminModeProvider>
      </ThemeProvider>
    </>
  );
}
